import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import './Auth.css';

function Signup({ onToggleMode, onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { signUp, refreshProfile } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!email || !password || !confirmPassword || !nickname.trim()) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    if (nickname.trim().length < 2) {
      setError('Nickname must be at least 2 characters');
      setIsLoading(false);
      return;
    }

    if (nickname.trim().length > 20) {
      setError('Nickname must be 20 characters or less');
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    // Store nickname in user metadata during signup
    const { data, error } = await signUp(email, password, { nickname: nickname.trim() });

    if (error) {
      setError(error.message);
      setIsLoading(false);
    } else {
      // Check if email confirmation is required
      if (data?.user && !data.session) {
        // Email confirmation required - save nickname after confirmation
        // Store nickname temporarily or let user set it after email confirmation
        setSuccess(true);
        setError('');
        setIsLoading(false);
        return;
      }

      // If we have a session, create profile after session is established
      if (data?.session) {
        // Wait a bit longer for session to be fully established and RLS to recognize the user
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Now try to save nickname to user profile
        // The session should be fully established now, so RLS should allow the insert
        try {
          const { data: profileData, error: profileError } = await supabase
            .from('user_profiles')
            .insert([
              {
                id: data.user.id,
                nickname: nickname.trim(),
                email: email.toLowerCase().trim()
              }
            ])
            .select();

          if (profileError) {
            console.error('Error saving nickname:', profileError);
            // If it's an RLS or permission error, the profile will be auto-created by AuthContext
            // with the nickname from metadata, so we don't need to show an error
            if (!profileError.message?.includes('row-level security') && !profileError.message?.includes('permission')) {
              setError(`Account created but failed to save nickname: ${profileError.message}. Please update it in your profile.`);
            } else {
              console.log('Profile will be auto-created by AuthContext');
            }
          } else {
            console.log('Nickname saved successfully:', profileData);
          }
        } catch (err) {
          console.error('Error creating profile:', err);
          // Don't show error - AuthContext will handle it
        }

        // Refresh profile to get the nickname
        if (refreshProfile) {
          await refreshProfile();
        }
        setTimeout(() => {
          if (onSuccess) onSuccess();
        }, 300);
      }
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2 className="auth-title">Check your email</h2>
          <div className="auth-success">
            We've sent a confirmation link to {email}. Please check your email to verify your account.
          </div>
          <button className="auth-link-btn" onClick={onToggleMode}>
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2 className="auth-title">Sign Up</h2>
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-input-group">
            <input
              type="text"
              className="auth-input"
              placeholder="Nickname (2-20 characters)"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              disabled={isLoading}
              required
              minLength={2}
              maxLength={20}
            />
          </div>
          <div className="auth-input-group">
            <input
              type="email"
              className="auth-input"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
            />
          </div>
          <div className="auth-input-group">
            <input
              type="password"
              className="auth-input"
              placeholder="Password (min 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              minLength={6}
            />
          </div>
          <div className="auth-input-group">
            <input
              type="password"
              className="auth-input"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              required
              minLength={6}
            />
          </div>
          {error && <div className="auth-error">{error}</div>}
          <button
            type="submit"
            className="auth-submit-btn"
            disabled={isLoading}
          >
            {isLoading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>
        <div className="auth-footer">
          <span>Already have an account? </span>
          <button className="auth-link-btn" onClick={onToggleMode}>
            Login
          </button>
        </div>
      </div>
    </div>
  );
}

export default Signup;

